import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Camera } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { AppHeader } from "@/components/AppHeader";
import { PhotoGallery } from "@/components/PhotoGallery";
import { PhotoEditor } from "@/components/PhotoEditor";
import { FileVault } from "@/components/FileVault";
import { RichTextEditor } from "@/components/RichTextEditor";
import { api, formatApiErrorDetail } from "@/lib/api";
import { photoHref } from "@/lib/api";
import { shrinkAll } from "@/lib/shrinkImage";

const SINK_ANGLES = [
  { value: "front", label: "Front" },
  { value: "side", label: "Side" },
  { value: "top", label: "Top" },
];

const TYPES = [
  "Undermount",
  "Farmhouse",
  "Vessel",
  "Drop-in",
  "Bar",
  "Utility",
];
const PORTAL_GROUPS = ["Contractor", "Designer", "Builder", "Commercial", "Regular"];
const BOWLS = ["Single", "Double", "1.5", "Triple", "None"];
const GAUGES = ["—", "16", "18", "20", "22"];
const MATERIALS = [
  "Stainless",
  "Composite",
  "Fireclay",
  "Cast Iron",
  "Copper",
  "Porcelain",
];
const EMPTY = {
  brand: "",
  model: "",
  description: "",
  sink_type: "Undermount",
  bowls: "Single",
  size: "",
  cutout_size: "",
  material: "Stainless",
  gauge: "—",
  supplier: "none",
  cost: "",
  sale_price: "",
  location: "",
  quantity: 0,
  min_level: 1,
  stocked: true,
  special_order: false,
  show_on_portal: false,
  group_prices: {},
  finishes: [],
  notes: "",
  template_url: "",
};

export default function SinkForm() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [form, setForm] = useState(EMPTY);
  const [suppliers, setSuppliers] = useState([]);
  const [brands, setBrands] = useState([]);
  const [busy, setBusy] = useState(false);
  const [photos, setPhotos] = useState([]);
  const [uploadingPhotos, setUploadingPhotos] = useState(false);
  const [editing, setEditing] = useState(null);
  const photoInputRef = useRef(null);

  const load = useCallback(async () => {
    const sups = await api.get("/suppliers");
    setSuppliers(sups.data);
    api
      .get("/brands")
      .then(({ data }) => setBrands(data.items || []))
      .catch(() => {});
    if (id) {
      const { data } = await api.get(`/sinks/${id}`);
      setForm({
        ...EMPTY,
        ...data,
        supplier: data.supplier || "none",
        gauge: data.gauge || "—",
        cost: data.cost ?? "",
        sale_price: data.sale_price ?? "",
      });
      setPhotos(data.photos || []);
    }
  }, [id]);

  useEffect(() => {
    load();
  }, [load]);

  const livePhotos = photos.filter((p) => !p.is_deleted);

  const uploadPhotos = async (e) => {
    const picked = Array.from(e.target.files || []);
    e.target.value = "";
    if (!picked.length || !id) return;
    setUploadingPhotos(true);
    const fd = new FormData();
    (await shrinkAll(picked)).forEach((f) => fd.append("files", f));
    try {
      const { data } = await api.post(`/sinks/${id}/photos`, fd);
      setPhotos(data.photos || []);
      toast.success(picked.length === 1 ? "Photo added" : `${picked.length} photos added`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Photo upload failed");
    }
    setUploadingPhotos(false);
  };

  const removePhoto = async (photoId) => {
    const { data } = await api.delete(`/sinks/${id}/photos/${photoId}`);
    setPhotos(data.photos || []);
    toast.success("Photo removed");
  };

  const setAngle = async (p, angle) => {
    try {
      const { data } = await api.patch(`/sinks/${id}/photos/${p.id}`, { angle });
      setPhotos(data.photos || []);
      toast.success(angle ? `Tagged as ${SINK_ANGLES.find((a) => a.value === angle)?.label}` : "Angle cleared");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not tag photo");
    }
  };

  const revertHd = async (photo) => {
    try {
      const { data } = await api.post("/photos/revert-hd", {
        collection: "sinks",
        doc_id: id,
        photo_id: photo.id,
      });
      setPhotos(data.photos || []);
      toast.success("Original photo restored");
    } catch {
      toast.error("Could not restore the original");
    }
  };

  const reorderPhotos = async (order) => {
    const { data } = await api.put(`/sinks/${id}/photos/order`, { order });
    setPhotos(data.photos || []);
  };

  const setCover = async (p) => {
    const order = [p.id, ...livePhotos.filter((x) => x.id !== p.id).map((x) => x.id)];
    const { data } = await api.put(`/sinks/${id}/photos/order`, { order });
    setPhotos(data.photos || []);
    toast.success("Cover photo updated");
  };

  const editPhoto = async (p) => {
    try {
      const { data } = await api.get(`/photos/${p.storage_path}`, { responseType: "blob" });
      setEditing({
        photoId: p.id,
        file: new File([data], p.original_filename || "sink.jpg", { type: data.type || "image/jpeg" }),
      });
    } catch {
      toast.error("Could not open that photo");
    }
  };

  const savePhoto = async (file, photoId) => {
    const fd = new FormData();
    fd.append("file", file);
    if (file.enhanced) fd.append("enhanced", "true");
    try {
      const { data } = await api.post(`/sinks/${id}/photos/${photoId}/replace`, fd);
      setPhotos(data.photos || []);
      toast.success("Photo updated");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not save the edit");
    }
  };

  const cutoutPhoto = async (photo) => {
    try {
      const { data } = await api.post(photo.cutout ? "/photos/cutout/undo" : "/photos/cutout", {
        collection: "sinks",
        doc_id: id,
        photo_id: photo.id,
      });
      setPhotos(data.photos || []);
      toast.success(photo.cutout ? "Original restored" : "Background removed");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not edit the photo");
    }
  };

  const addFinish = () =>
    setForm((f) => ({
      ...f,
      finishes: [
        ...(f.finishes || []),
        {
          id:
            (typeof crypto !== "undefined" && crypto.randomUUID?.()) ||
            String(Date.now()),
          name: "",
          sku: "",
          photo_id: livePhotos[0]?.id || "",
        },
      ],
    }));
  const updateFinish = (i, patch) =>
    setForm((f) => ({
      ...f,
      finishes: (f.finishes || []).map((x, n) => (n === i ? { ...x, ...patch } : x)),
    }));
  const removeFinish = (i) =>
    setForm((f) => ({
      ...f,
      finishes: (f.finishes || []).filter((_, n) => n !== i),
    }));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    const payload = {
      ...form,
      supplier: form.supplier === "none" ? "" : form.supplier,
      gauge: form.gauge === "—" ? "" : form.gauge,
      cost: form.cost === "" ? null : Number(form.cost),
      sale_price: form.sale_price === "" ? null : Number(form.sale_price),
      quantity: Number(form.quantity) || 0,
      min_level: Number(form.min_level) || 0,
      stocked: form.stocked !== false,
      special_order: form.special_order === true,
      show_on_portal: form.show_on_portal === true,
      group_prices: PORTAL_GROUPS.reduce((acc, g) => {
        const v = (form.group_prices || {})[g];
        if (v !== "" && v !== null && v !== undefined) acc[g] = Number(v);
        return acc;
      }, {}),
      finishes: (form.finishes || []).filter(
        (x) => (x.name || "").trim() || (x.sku || "").trim() || x.photo_id,
      ),
    };
    try {
      if (id) await api.put(`/sinks/${id}`, payload);
      else {
        const { data } = await api.post("/sinks", payload);
        toast.success("Sink added to stock");
        navigate(`/inventory/sinks/${data.id}`);
        setBusy(false);
        return;
      }
      toast.success("Sink updated");
      navigate(`/inventory/sinks/${id}`);
    } catch (err) {
      toast.error(
        formatApiErrorDetail(err.response?.data?.detail) ||
          "Could not save sink",
      );
    }
    setBusy(false);
  };

  const field = (k, label, props = {}) => (
    <div className="space-y-2">
      <Label className="text-xs uppercase tracking-widest text-zinc-400">
        {label}
      </Label>
      <Input
        data-testid={`sink-${k.replace(/_/g, "-")}-input`}
        value={form[k]}
        onChange={(e) => setForm({ ...form, [k]: e.target.value })}
        className="h-12 border-zinc-800 bg-zinc-900 text-base"
        {...props}
      />
    </div>
  );

  const dropdown = (k, label, options) => (
    <div className="space-y-2">
      <Label className="text-xs uppercase tracking-widest text-zinc-400">
        {label}
      </Label>
      <Select
        value={form[k]}
        onValueChange={(v) => setForm({ ...form, [k]: v })}
      >
        <SelectTrigger
          data-testid={`sink-${k.replace(/_/g, "-")}-select`}
          className="h-12 border-zinc-800 bg-zinc-900 text-base"
        >
          <SelectValue />
        </SelectTrigger>
        <SelectContent className="border-zinc-800 bg-zinc-900">
          {options.map((o) => (
            <SelectItem key={o} value={o}>
              {o}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );

  return (
    <div className="grain min-h-screen">
      <AppHeader
        title={id ? "Inventory / Edit Sink" : "Inventory / New Sink"}
      />
      <main className="relative z-10 pb-28 pt-6">
        <Link
          to="/inventory/sinks"
          data-testid="back-to-sinks"
          className="inline-flex items-center gap-2 text-xs uppercase tracking-widest text-zinc-500 hover:text-white"
        >
          <ArrowLeft size={14} weight="bold" /> All sinks
        </Link>
        <h1 className="mt-3 text-2xl font-bold tracking-tight sm:text-3xl">
          {id ? "Edit Sink" : "New Sink"}
        </h1>

        <div className="mt-6 grid gap-6 lg:grid-cols-[minmax(0,1fr)_380px] lg:items-start">
          <form
            onSubmit={submit}
            data-testid="sink-form"
            className="space-y-6"
          >
          <div className="grid gap-4 rounded-lg border border-zinc-800 bg-zinc-900 p-4 sm:grid-cols-2">
            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-widest text-zinc-400">
                Brand
              </Label>
              <Input
                data-testid="sink-brand-input"
                list="sink-brand-options"
                value={form.brand}
                onChange={(e) => setForm({ ...form, brand: e.target.value })}
                placeholder="Blanco"
                className="h-12 border-zinc-800 bg-zinc-900 text-base"
              />
              <datalist id="sink-brand-options">
                {[
                  ...brands.filter(
                    (b) => b.supplier && b.supplier === form.supplier,
                  ),
                  ...brands.filter(
                    (b) => !b.supplier || b.supplier !== form.supplier,
                  ),
                ].map((b) => (
                  <option key={`${b.supplier}-${b.name}`} value={b.name}>
                    {b.supplier ? `${b.name} — ${b.supplier}` : b.name}
                  </option>
                ))}
              </datalist>
            </div>
            {field("model", "Model / SKU (item code)", {
              required: true,
              placeholder: "440150",
            })}
            <div className="sm:col-span-2 space-y-2">
              <Label className="text-xs uppercase tracking-widest text-zinc-400">
                Description
              </Label>
              <RichTextEditor
                value={form.description}
                onChange={(html) => setForm({ ...form, description: html })}
                dataTestId="sink-description-editor"
                mergeFields={[]}
              />
              <p className="text-[11px] text-zinc-500">
                Shown on the public and customer portal sink pages.
              </p>
            </div>
            {dropdown("sink_type", "Type", TYPES)}
            {dropdown("bowls", "Bowls", BOWLS)}
            {field("size", "Size", { placeholder: '30" x 18"' })}
            {field("cutout_size", "Cutout / inner size", {
              placeholder: '28½" x 16½" (or Ø 16")',
            })}
            {dropdown("material", "Material", MATERIALS)}
            {dropdown("gauge", "Gauge", GAUGES)}
            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-widest text-zinc-400">
                Supplier
              </Label>
              <Select
                value={form.supplier}
                onValueChange={(v) => setForm({ ...form, supplier: v })}
              >
                <SelectTrigger
                  data-testid="sink-supplier-select"
                  className="h-12 border-zinc-800 bg-zinc-900 text-base"
                >
                  <SelectValue placeholder="No supplier" />
                </SelectTrigger>
                <SelectContent className="border-zinc-800 bg-zinc-900">
                  <SelectItem value="none">No supplier</SelectItem>
                  {suppliers.map((s) => (
                    <SelectItem key={s.id} value={s.name}>
                      {s.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {field("location", "Shelf / location", {
              placeholder: "Sink rack B",
            })}
          </div>

          <div className="space-y-4 rounded-lg border border-zinc-800 bg-zinc-900 p-4">
            <label
              data-testid="sink-stocked-toggle"
              className="flex cursor-pointer items-center gap-3 rounded-md border border-zinc-800 bg-zinc-950 p-3"
            >
              <input
                type="checkbox"
                data-testid="sink-stocked-input"
                checked={form.stocked !== false}
                onChange={(e) =>
                  setForm({ ...form, stocked: e.target.checked })
                }
                className="h-5 w-5 accent-indigo-500"
              />
              <span>
                <span className="block text-sm font-semibold">
                  Track inventory
                </span>
                <span className="block text-xs text-zinc-500">
                  On: counted in stock and included in low-stock alerts. Off:
                  shown as supplier stock — not counted, no alerts
                </span>
              </span>
            </label>
            <label
              data-testid="sink-special-order-toggle"
              className="flex cursor-pointer items-center gap-3 rounded-md border border-zinc-800 bg-zinc-950 p-3"
            >
              <input
                type="checkbox"
                data-testid="sink-special-order-input"
                checked={form.special_order === true}
                onChange={(e) => setForm({ ...form, special_order: e.target.checked })}
                className="h-5 w-5 accent-indigo-500"
              />
              <span>
                <span className="block text-sm font-semibold">Supplier stock</span>
                <span className="block text-xs text-zinc-500">
                  On: brought in from the supplier per job (shown as &ldquo;Supplier Stock&rdquo; in the portal). Off: a stock sink we keep on hand
                </span>
              </span>
            </label>
            <div className="grid gap-4 sm:grid-cols-3">
              {field("cost", "Cost ($)", { type: "number", step: "0.01" })}
              {field("sale_price", "Sale price ($)", {
                type: "number",
                step: "0.01",
              })}
              {form.stocked !== false &&
                field("quantity", "On hand", { type: "number", min: "0" })}
              {form.stocked !== false &&
                field("min_level", "Min level", { type: "number", min: "0" })}
            </div>
          </div>

          <div className="space-y-4 rounded-lg border border-zinc-800 bg-zinc-900 p-4">
            <label
              data-testid="sink-portal-toggle"
              className="flex cursor-pointer items-center gap-3 rounded-md border border-zinc-800 bg-zinc-950 p-3"
            >
              <input
                type="checkbox"
                data-testid="sink-portal-input"
                checked={form.show_on_portal === true}
                onChange={(e) =>
                  setForm({ ...form, show_on_portal: e.target.checked })
                }
                className="h-5 w-5 accent-indigo-500"
              />
              <span>
                <span className="block text-sm font-semibold">
                  Show in customer portal
                </span>
                <span className="block text-xs text-zinc-500">
                  On: customers can browse this sink in the portal and ask to hold it
                </span>
              </span>
            </label>
            {form.show_on_portal === true && (
              <div>
                <Label className="text-xs uppercase tracking-widest text-zinc-400">
                  Portal price per customer group ($)
                </Label>
                <p className="mt-1 text-xs text-zinc-500">
                  Shown in the portal only when “Show inventory prices” is on and the
                  customer’s group is selected in Portal settings. Leave a group blank
                  to hide its price.
                </p>
                <div className="mt-3 grid gap-3 sm:grid-cols-3">
                  {PORTAL_GROUPS.map((g) => (
                    <div key={g} className="space-y-1">
                      <Label className="text-[11px] uppercase tracking-widest text-zinc-500">
                        {g}
                      </Label>
                      <Input
                        type="number"
                        step="0.01"
                        min="0"
                        data-testid={`sink-group-price-${g.toLowerCase()}`}
                        value={(form.group_prices || {})[g] ?? ""}
                        onChange={(e) =>
                          setForm({
                            ...form,
                            group_prices: {
                              ...(form.group_prices || {}),
                              [g]: e.target.value,
                            },
                          })
                        }
                        className="h-11 border-zinc-800 bg-zinc-950 text-base"
                      />
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {id && (
            <div
              className="space-y-4 rounded-lg border border-zinc-800 bg-zinc-900 p-4"
              data-testid="sink-finishes-section"
            >
              <div className="flex items-center justify-between gap-3">
                <Label className="text-xs uppercase tracking-widest text-zinc-400">
                  Finishes / swatches
                </Label>
                <Button
                  type="button"
                  data-testid="sink-add-finish"
                  variant="outline"
                  disabled={!livePhotos.length}
                  onClick={addFinish}
                  className="h-10 border-zinc-700 bg-transparent text-xs uppercase tracking-widest hover:bg-zinc-800"
                >
                  Add finish
                </Button>
              </div>
              <p className="text-xs text-zinc-500">
                Different colour / finish options of this sink. Each swatch shows a
                thumbnail, name and SKU in the customer portal. Pick a thumbnail from
                this sink&apos;s uploaded photos.
              </p>
              {!livePhotos.length && (
                <p className="text-xs text-amber-500/80">
                  Upload a photo first — swatches use a photo as their thumbnail.
                </p>
              )}
              {(form.finishes || []).map((fin, i) => (
                <div
                  key={fin.id}
                  data-testid={`sink-finish-row-${i}`}
                  className="space-y-3 rounded-md border border-zinc-800 bg-zinc-950 p-3"
                >
                  <div className="grid gap-3 sm:grid-cols-2">
                    <Input
                      data-testid={`sink-finish-name-${i}`}
                      value={fin.name}
                      onChange={(e) => updateFinish(i, { name: e.target.value })}
                      placeholder="Finish name e.g. Brushed Nickel"
                      className="h-11 border-zinc-800 bg-zinc-900 text-base"
                    />
                    <Input
                      data-testid={`sink-finish-sku-${i}`}
                      value={fin.sku}
                      onChange={(e) => updateFinish(i, { sku: e.target.value })}
                      placeholder="SKU / code"
                      className="h-11 border-zinc-800 bg-zinc-900 text-base"
                    />
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {livePhotos.map((p) => (
                      <button
                        key={p.id}
                        type="button"
                        data-testid={`sink-finish-${i}-photo-${p.id}`}
                        onClick={() => updateFinish(i, { photo_id: p.id })}
                        className={`relative h-12 w-12 overflow-hidden rounded-md border-2 transition-colors duration-150 ${
                          fin.photo_id === p.id
                            ? "border-[#c39e6d]"
                            : "border-zinc-800 hover:border-zinc-600"
                        }`}
                      >
                        <img
                          src={photoHref(p.thumb_path || p.storage_path)}
                          alt=""
                          className="h-full w-full object-cover"
                        />
                      </button>
                    ))}
                  </div>
                  <button
                    type="button"
                    data-testid={`sink-finish-remove-${i}`}
                    onClick={() => removeFinish(i)}
                    className="text-xs font-semibold uppercase tracking-widest text-rose-400 hover:text-rose-300"
                  >
                    Remove finish
                  </button>
                </div>
              ))}
            </div>
          )}

          <div className="space-y-2 rounded-lg border border-zinc-800 bg-zinc-900 p-4">
            <Label className="text-xs uppercase tracking-widest text-zinc-400">
              Spec sheet / cut-out template link
            </Label>
            <Input
              data-testid="sink-template-url-input"
              type="url"
              value={form.template_url || ""}
              onChange={(e) => setForm({ ...form, template_url: e.target.value })}
              placeholder="https://… (manufacturer PDF)"
              className="border-zinc-800 bg-zinc-950"
            />
            <p className="text-xs text-zinc-500">
              Printed next to this sink's cut-out on quote PDFs so fabricators can open the template.
            </p>
          </div>

          <div className="space-y-2 rounded-lg border border-zinc-800 bg-zinc-900 p-4">
            <Label className="text-xs uppercase tracking-widest text-zinc-400">
              Notes
            </Label>
            <Textarea
              data-testid="sink-notes-input"
              rows={3}
              value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
              placeholder="Cutout template, clips included…"
              className="border-zinc-800 bg-zinc-950"
            />
          </div>

          <div className="flex gap-3">
            <Button
              type="submit"
              data-testid="save-sink-button"
              disabled={busy}
              className="h-12 flex-1 bg-indigo-500 text-xs font-bold uppercase tracking-widest text-white hover:bg-indigo-400"
            >
              {busy ? "Saving…" : id ? "Save Sink" : "Add Sink"}
            </Button>
            <Button
              type="button"
              data-testid="cancel-sink-button"
              variant="outline"
              onClick={() => navigate("/inventory/sinks")}
              className="h-12 border-zinc-700 bg-transparent text-xs uppercase tracking-widest"
            >
              Cancel
            </Button>
          </div>
        </form>

          <aside className="space-y-6" data-testid="sink-media-panel">
            {id ? (
              <>
                <div className="rounded-lg border border-zinc-800 bg-zinc-900 p-4" data-testid="sink-form-photos-panel">
                  <div className="flex items-center justify-between gap-3">
                    <div className="text-xs uppercase tracking-widest text-zinc-400">
                      Photos ({livePhotos.length})
                    </div>
                    <Button
                      type="button"
                      data-testid="sink-form-add-photos"
                      variant="outline"
                      disabled={uploadingPhotos}
                      onClick={() => photoInputRef.current?.click()}
                      className="h-10 border-zinc-700 bg-transparent text-xs uppercase tracking-widest hover:bg-zinc-800"
                    >
                      <Camera size={14} weight="bold" className="mr-2" />
                      {uploadingPhotos ? "Uploading…" : "Add Photos"}
                    </Button>
                    <input
                      ref={photoInputRef}
                      data-testid="sink-form-photo-input"
                      type="file"
                      accept="image/*"
                      multiple
                      className="hidden"
                      onChange={uploadPhotos}
                    />
                  </div>
                  <PhotoGallery
                    photos={livePhotos}
                    onRemove={removePhoto}
                    onRevertHd={revertHd}
                    onReorder={reorderPhotos}
                    onSetCover={setCover}
                    onEdit={editPhoto}
                    onCutout={cutoutPhoto}
                    angleOptions={SINK_ANGLES}
                    onAngle={setAngle}
                    testPrefix="sink-form-photo"
                  />
                  {editing && (
                    <PhotoEditor
                      file={editing.file}
                      onDone={(f) => {
                        const target = editing.photoId;
                        setEditing(null);
                        savePhoto(f, target);
                      }}
                      onClose={() => setEditing(null)}
                    />
                  )}
                  <p className="mt-3 text-xs text-zinc-500">
                    Drag to reorder, tap the star to set the cover, or the wand to crop, enhance &amp; remove the background with AI.
                  </p>
                </div>

                <FileVault
                  entity="sinks"
                  entityId={id}
                  title="Spec sheets & documents"
                />
              </>
            ) : (
              <div
                className="rounded-lg border border-dashed border-zinc-800 bg-zinc-900/60 p-6 text-center"
                data-testid="sink-media-locked"
              >
                <Camera size={24} weight="bold" className="mx-auto text-zinc-600" />
                <p className="mt-3 text-sm text-zinc-400">
                  Add the sink first, then upload photos and spec sheets here.
                </p>
              </div>
            )}
          </aside>
        </div>
      </main>
    </div>
  );
}
